import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { 
  FileText, 
  MagnifyingGlass, 
  PaperPlaneTilt,
  CheckCircle,
  XCircle,
  Clock,
  CurrencyDollar,
  ArrowClockwise,
  CaretDown,
  Eye,
  Receipt,
  Stack
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ClaimsManagementPage() {
  const [claims, setClaims] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchClaims();
    fetchInvoices();
  }, [statusFilter]);

  const fetchClaims = async () => {
    try {
      const url = statusFilter ? `/billing/claims?status=${statusFilter}` : '/billing/claims';
      const response = await api.get(url);
      setClaims(response.data.claims || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching claims:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await api.get('/billing/invoices');
      // Filter to invoices that can have claims submitted (have insurance, not already claimed)
      const claimableInvoices = response.data.filter(inv => 
        inv.insurance_id && !inv.claim_id && inv.status !== 'paid'
      );
      setInvoices(claimableInvoices);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching invoices:', error);
    }
  };

  const submitClaim = async () => {
    if (!selectedInvoice) return;
    
    setSubmitting(true);
    try {
      const response = await api.post('/billing/claims/submit', {
        invoice_id: selectedInvoice.id,
        place_of_service: '11'
      });
      toast.success(response.data.message);
      setShowSubmitModal(false);
      setSelectedInvoice(null);
      fetchClaims();
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit claim');
    } finally {
      setSubmitting(false);
    }
  };

  const submitBatchClaims = async () => {
    if (selectedInvoices.length === 0) return;
    
    setSubmitting(true);
    try {
      const response = await api.post('/billing/claims/batch-submit', {
        invoice_ids: selectedInvoices,
        place_of_service: '11',
        notes: 'End-of-day batch submission'
      });
      
      const { summary, results } = response.data;
      
      if (summary.submitted > 0) {
        toast.success(`${summary.submitted} claims submitted successfully`);
      }
      if (summary.failed > 0) {
        toast.error(`${summary.failed} claims failed to submit`);
      }
      if (summary.skipped > 0) {
        toast.info(`${summary.skipped} invoices skipped (already claimed)`);
      }
      
      setShowBatchModal(false);
      setSelectedInvoices([]);
      fetchClaims();
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Batch submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleInvoiceSelection = (invoiceId) => {
    setSelectedInvoices(prev => 
      prev.includes(invoiceId)
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const selectAllInvoices = () => {
    if (selectedInvoices.length === invoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(invoices.map(inv => inv.id));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      submitted: 'bg-blue-100 text-blue-700',
      accepted: 'bg-teal-100 text-teal-700',
      denied: 'bg-red-100 text-red-700',
      paid: 'bg-green-100 text-green-700',
      appealed: 'bg-purple-100 text-purple-700',
      cancelled: 'bg-slate-100 text-slate-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle className="text-green-500" size={20} />;
      case 'denied': return <XCircle className="text-red-500" size={20} />;
      case 'submitted':
      case 'pending': return <Clock className="text-yellow-500" size={20} />;
      default: return <FileText className="text-slate-400" size={20} />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Calculate summary stats
  const stats = {
    total: claims.length,
    pending: claims.filter(c => c.status === 'pending' || c.status === 'submitted').length,
    paid: claims.filter(c => c.status === 'paid').length,
    denied: claims.filter(c => c.status === 'denied').length,
    totalCharges: claims.reduce((sum, c) => sum + (c.total_charges || 0), 0),
    totalPaid: claims.reduce((sum, c) => sum + (c.paid_amount || 0), 0)
  };

  return (
    <div className="p-8" data-testid="claims-management-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Claims Management
        </h1>
        <p className="text-slate-500">Submit and track insurance claims</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="card-clinical p-4">
          <p className="text-sm text-slate-500">Total Claims</p>
          <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
        </div>
        <div className="card-clinical p-4">
          <p className="text-sm text-slate-500">Pending</p>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
        </div>
        <div className="card-clinical p-4">
          <p className="text-sm text-slate-500">Paid</p>
          <p className="text-2xl font-bold text-green-600">{stats.paid}</p>
        </div>
        <div className="card-clinical p-4">
          <p className="text-sm text-slate-500">Denied</p>
          <p className="text-2xl font-bold text-red-600">{stats.denied}</p>
        </div>
        <div className="card-clinical p-4">
          <p className="text-sm text-slate-500">Total Collected</p>
          <p className="text-2xl font-bold text-teal-600">{formatCurrency(stats.totalPaid)}</p>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-clinical w-48"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="submitted">Submitted</option>
            <option value="accepted">Accepted</option>
            <option value="paid">Paid</option>
            <option value="denied">Denied</option>
            <option value="appealed">Appealed</option>
          </select>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={() => setShowBatchModal(true)}
            disabled={invoices.length === 0}
            className="btn-outline flex items-center gap-2"
          >
            <Stack size={18} />
            Batch Submit
          </button>
          <button
            onClick={() => setShowSubmitModal(true)}
            disabled={invoices.length === 0}
            className="btn-teal flex items-center gap-2"
          >
            <PaperPlaneTilt size={18} />
            Submit New Claim
          </button>
        </div>
      </div>

      {/* Claims Table */}
      <div className="card-clinical overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Claim #</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Patient</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Insurance</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Service Date</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Charges</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Paid</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Status</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="py-8 text-center text-slate-500">
                  <ArrowClockwise size={24} className="animate-spin mx-auto mb-2" />
                  Loading claims...
                </td>
              </tr>
            ) : claims.length === 0 ? (
              <tr>
                <td colSpan="8" className="py-8 text-center text-slate-500">
                  <FileText size={48} className="mx-auto mb-2 text-slate-300" />
                  No claims found
                </td>
              </tr>
            ) : (
              claims.map((claim) => (
                <tr key={claim.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <span className="font-mono text-sm text-teal-600">{claim.claim_number}</span>
                  </td>
                  <td className="py-3 px-4">
                    <p className="font-medium text-slate-900">{claim.patient_name}</p>
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-sm text-slate-700">{claim.insurance_name}</p>
                    <p className="text-xs text-slate-500">#{claim.policy_number}</p>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-600">
                    {formatDate(claim.service_date)}
                  </td>
                  <td className="py-3 px-4 text-right font-medium">
                    {formatCurrency(claim.total_charges)}
                  </td>
                  <td className="py-3 px-4 text-right font-medium text-green-600">
                    {claim.paid_amount ? formatCurrency(claim.paid_amount) : '-'}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {getStatusBadge(claim.status)}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <button
                      onClick={() => setSelectedClaim(claim)}
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye size={18} className="text-slate-500" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Submit Claim Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Submit New Claim</h2>
            
            <p className="text-slate-600 mb-4">Select an invoice to submit as a claim:</p>
            
            <div className="max-h-64 overflow-y-auto space-y-2 mb-6">
              {invoices.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No claimable invoices available</p>
              ) : (
                invoices.map((inv) => (
                  <button
                    key={inv.id}
                    onClick={() => setSelectedInvoice(inv)}
                    className={`w-full p-4 rounded-lg border text-left transition-colors ${
                      selectedInvoice?.id === inv.id
                        ? 'bg-teal-50 border-teal-300'
                        : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{inv.patient_name}</p>
                        <p className="text-sm text-slate-500">{formatDate(inv.created_at)}</p>
                      </div>
                      <p className="font-bold text-teal-600">{formatCurrency(inv.subtotal)}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowSubmitModal(false);
                  setSelectedInvoice(null);
                }}
                className="flex-1 btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={submitClaim}
                disabled={!selectedInvoice || submitting}
                className="flex-1 btn-teal flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <ArrowClockwise size={18} className="animate-spin" />
                ) : (
                  <PaperPlaneTilt size={18} />
                )}
                Submit Claim
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Claim Detail Modal */}
      {selectedClaim && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Claim Details</h2>
                <p className="text-teal-600 font-mono">{selectedClaim.claim_number}</p>
              </div>
              {getStatusBadge(selectedClaim.status)}
            </div>
            
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-sm font-medium text-slate-500 mb-2">Patient</h3>
                <p className="font-medium text-slate-900">{selectedClaim.patient_name}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-500 mb-2">Insurance</h3>
                <p className="font-medium text-slate-900">{selectedClaim.insurance_name}</p>
                <p className="text-sm text-slate-500">Policy: {selectedClaim.policy_number}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-500 mb-2">Service Date</h3>
                <p className="text-slate-900">{formatDate(selectedClaim.service_date)}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-500 mb-2">Submitted</h3>
                <p className="text-slate-900">{formatDate(selectedClaim.submission_date)}</p>
              </div>
            </div>
            
            {/* Diagnosis Codes */}
            {selectedClaim.diagnosis_codes?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-slate-500 mb-2">Diagnosis Codes</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedClaim.diagnosis_codes.map((dx, idx) => (
                    <span key={idx} className="px-2 py-1 bg-red-50 text-red-700 rounded text-sm">
                      {dx.code}: {dx.description?.substring(0, 30)}...
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Procedure Codes */}
            {selectedClaim.procedure_codes?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-slate-500 mb-2">Procedure Codes</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Code</th>
                      <th className="text-left py-2">Description</th>
                      <th className="text-right py-2">Charges</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedClaim.procedure_codes.map((proc, idx) => (
                      <tr key={idx} className="border-b border-slate-100">
                        <td className="py-2 font-mono text-blue-600">{proc.code}</td>
                        <td className="py-2 text-slate-600">{proc.description?.substring(0, 40)}...</td>
                        <td className="py-2 text-right">{formatCurrency(proc.charges)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Financial Summary */}
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-slate-900 mb-3">Financial Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-600">Total Charges</span>
                  <span className="font-medium">{formatCurrency(selectedClaim.total_charges)}</span>
                </div>
                {selectedClaim.allowed_amount && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Allowed Amount</span>
                    <span className="font-medium">{formatCurrency(selectedClaim.allowed_amount)}</span>
                  </div>
                )}
                {selectedClaim.paid_amount && (
                  <div className="flex justify-between text-green-600">
                    <span>Paid Amount</span>
                    <span className="font-bold">{formatCurrency(selectedClaim.paid_amount)}</span>
                  </div>
                )}
                {selectedClaim.patient_responsibility && (
                  <div className="flex justify-between text-amber-600">
                    <span>Patient Responsibility</span>
                    <span className="font-medium">{formatCurrency(selectedClaim.patient_responsibility)}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Denial Info */}
            {selectedClaim.status === 'denied' && selectedClaim.denial_reason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-red-800 mb-2">Denial Information</h3>
                <p className="text-red-700">{selectedClaim.denial_reason}</p>
                {selectedClaim.denial_code && (
                  <p className="text-sm text-red-600 mt-1">Code: {selectedClaim.denial_code}</p>
                )}
              </div>
            )}
            
            {/* Status History */}
            {selectedClaim.status_history?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-slate-500 mb-2">Status History</h3>
                <div className="space-y-2">
                  {selectedClaim.status_history.map((entry, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm">
                      {getStatusIcon(entry.status)}
                      <span className="text-slate-600">{formatDate(entry.date)}</span>
                      <span className="font-medium">{entry.status}</span>
                      {entry.notes && <span className="text-slate-500">- {entry.notes}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <button
              onClick={() => setSelectedClaim(null)}
              className="w-full btn-outline"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Batch Submit Modal */}
      {showBatchModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-teal-100 rounded-lg">
                <Stack size={24} className="text-teal-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">Batch Claim Submission</h2>
                <p className="text-sm text-slate-500">Submit multiple claims at once</p>
              </div>
            </div>
            
            {invoices.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No claimable invoices available</p>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedInvoices.length === invoices.length}
                      onChange={selectAllInvoices}
                      className="w-4 h-4 text-teal-600"
                    />
                    <span className="text-sm font-medium">Select All ({invoices.length})</span>
                  </label>
                  <span className="text-sm text-slate-500">
                    {selectedInvoices.length} selected • {formatCurrency(
                      invoices
                        .filter(inv => selectedInvoices.includes(inv.id))
                        .reduce((sum, inv) => sum + (inv.subtotal || 0), 0)
                    )} total
                  </span>
                </div>
                
                <div className="max-h-64 overflow-y-auto space-y-2 mb-6 border rounded-lg p-2">
                  {invoices.map((inv) => (
                    <label
                      key={inv.id}
                      className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedInvoices.includes(inv.id)
                          ? 'bg-teal-50 border border-teal-200'
                          : 'bg-slate-50 hover:bg-slate-100 border border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedInvoices.includes(inv.id)}
                          onChange={() => toggleInvoiceSelection(inv.id)}
                          className="w-4 h-4 text-teal-600"
                        />
                        <div>
                          <p className="font-medium text-slate-900">{inv.patient_name}</p>
                          <p className="text-sm text-slate-500">{formatDate(inv.created_at)}</p>
                        </div>
                      </div>
                      <p className="font-bold text-teal-600">{formatCurrency(inv.subtotal)}</p>
                    </label>
                  ))}
                </div>
                
                {selectedInvoices.length > 0 && (
                  <div className="bg-teal-50 rounded-lg p-4 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-teal-800">Ready to Submit</span>
                      <span className="text-2xl font-bold text-teal-700">
                        {selectedInvoices.length} claims
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowBatchModal(false);
                  setSelectedInvoices([]);
                }}
                className="flex-1 btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={submitBatchClaims}
                disabled={selectedInvoices.length === 0 || submitting}
                className="flex-1 btn-teal flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <ArrowClockwise size={18} className="animate-spin" />
                ) : (
                  <PaperPlaneTilt size={18} />
                )}
                Submit {selectedInvoices.length} Claim{selectedInvoices.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
